import * as mapUtils from './map-utils';
import differenceWith from 'lodash/differenceWith';
import throttle from 'lodash/throttle';
import trackPinPreviewClicked from 'segment/events/search-pin-preview-clicked';
// Note: The googleMapWidget function uses function declarations instead of arrow functions so that the functions
// are hoisted and can be used before they are defined to improve readability.
/**
 * Creates a new GoogleMapWidget instance with the specified options.
 *
 * @param {object} options - The options for the new GoogleMapWidget instance.
 * @param {HTMLElement} options.container - The container element for the Google Map.
 * @param {LatLng} options.centerLatLng - The center coordinates of the Google Map.
 * @param {string} options.centerTitle - The title of the center marker.
 * @param {boolean} options.kmlMapLayerEnabled - Whether the KML map layer is enabled.
 * @param {string} options.kmlMapLayerURL - The URL of the KML map layer.
 * @param {string} options.searchRequestId - The ID of the search request.
 * @param {Spot[]} options.spots - The spots to display on the Google Map.
 * @param {number} options.zoom - The zoom level of the Google Map.
 * @param {Function} options.getZIndex - The function to get the z-index of a marker.
 * @param {Function} options.onSpotDetail - The callback to be called when spot details are needed.
 * @param {Function} options.onPan - The callback to be called when the map is panned.
 * @param {Function} options.onZoom - The callback to be called when the map is zoomed.
 * @param {Function} options.onGoogleMapLoaded - The callback to be called when the Google Map is loaded.
 * @param {boolean} options.showAllSpots - The boolean value indicating whether the all spots needs to be shown in the map.
 */
const googleMapWidget = ({ container, googleMapsId, centerLatLng: _centerLatLng, centerTitle: _centerTitle, kmlMapLayerEnabled, kmlMapLayerURL, searchRequestId: _searchRequestId, spots: _spots, zoom: _zoom, getZIndex, onSpotDetail: onSpotDetailCallback, onPan: onPanCallback, onZoom, onGoogleMapLoaded, onPreviewSpot: onPreviewSpotCallback, showAllSpots, }) => {
    const maps = mapUtils.getMapsLibrary();
    const markers = {};
    const zoomThrottle = throttle(onZoomChanged, 1000);
    let previewSpot = null;
    let centerLatLng = _centerLatLng;
    let centerMarker = null;
    let centerTitle = _centerTitle;
    let isDraggingTimeout = null;
    let isDragging = false;
    let isAutoZooming = false;
    let infoWindow = null;
    let kmlLayer = null;
    let map = null;
    let needsFitToBounds = true;
    let spots = _spots || [];
    let searchRequestId = _searchRequestId;
    let zoom = _zoom;
    let prevZoom = null;
    function onOpenSpotDetails(spot) {
        if (isDragging) {
            return;
        }
        if (onSpotDetailCallback) {
            onSpotDetailCallback(spot);
        }
    }
    function init() {
        map = mapUtils.initializeMap({
            container,
            googleMapsId,
            maps,
            zoom,
            center: centerLatLng,
            idleCallback: () => {
                onGoogleMapLoaded(returnMapToDestination);
            },
            tilesLoadedCallback: () => {
                if (spots.length > 0) {
                    fitMapToMarkers();
                }
            },
            onDragStart,
            onDragEnd,
            onZoomChanged: zoomThrottle,
            onSpotHover,
            onMapClick: () => {
                closeInfoWindow();
            },
        });
        infoWindow = mapUtils.initializeInfoWindow({
            maps,
            map,
            onClose: () => {
                // clear hover state and show marker for preview spot
                if (previewSpot) {
                    const marker = markers[previewSpot.spotId];
                    mapUtils.clearHoverStateFromMarker({
                        marker,
                        spotId: previewSpot.spotId,
                    });
                    mapUtils.showMarker({
                        marker,
                        map,
                    });
                }
            },
        });
        if (kmlMapLayerEnabled && kmlMapLayerURL) {
            addKMLLayer(kmlMapLayerURL);
        }
        // add center marker
        const { marker } = mapUtils.newMarkerAndLatLng({
            lat: centerLatLng.lat,
            lng: centerLatLng.lng,
            title: centerTitle,
            maps,
            map,
        });
        centerMarker = marker;
    }
    /**
     * Returns the map to the destination center and zoom level.
     */
    function returnMapToDestination() {
        mapUtils.returnMapToDestination({
            map,
            maps,
            center: centerLatLng,
            onPan,
        });
    }
    /**
     * Callback function to be called when the map is panned.
     */
    function onPan() {
        const options = mapUtils.getMapOptions({
            map,
            center: centerLatLng,
        });
        onPanCallback(options);
    }
    /**
     * Sets the center coordinates and title of the map.
     *
     * @param {number} lat - The latitude of the new center coordinates.
     * @param {number} lng - The longitude of the new center coordinates.
     * @param {string} title - The new title of the center marker.
     */
    function setCenterLatLngAndTitle(lat, lng, title) {
        if (centerLatLng.lat === lat &&
            centerLatLng.lng === lng &&
            centerTitle === title) {
            return;
        }
        const { position, marker } = mapUtils.newMarkerAndLatLng({
            lat,
            lng,
            title,
            maps,
            map,
        });
        centerLatLng = position;
        centerTitle = title;
        // clear old marker
        if (centerMarker) {
            mapUtils.hideMarker({ marker: centerMarker });
        }
        centerMarker = marker;
    }
    function onPreviewSpot(spot) {
        previewSpot = spot;
        // clear hover state from all markers and make them visible
        Object.entries(markers).forEach(([spotId, marker]) => {
            mapUtils.clearHoverStateFromMarker({
                marker,
                spotId,
            });
            if (isPreviewSpotId(spotId)) {
                mapUtils.hideMarker({ marker });
            }
            else {
                mapUtils.showMarker({ marker, map });
            }
        });
        onPreviewSpotCallback(previewSpot);
    }
    /**
     * Sets the zoom level of the map.
     *
     * @param {number} newZoom - The new zoom level of the map.
     */
    function setZoom(newZoom) {
        if (zoom !== newZoom) {
            prevZoom = zoom;
            zoom = newZoom;
            closeInfoWindow();
            mapUtils.setZoom({ zoom, map });
        }
    }
    /**
     * Sets the ID of the search request.
     *
     * @param {string} newSearchRequestId - The new ID of the search request.
     * @returns {boolean} Whether the search request ID was changed.
     */
    function setSearchRequestId(newSearchRequestId) {
        if (searchRequestId !== newSearchRequestId) {
            searchRequestId = newSearchRequestId;
            needsFitToBounds = true;
            return true;
        }
        return false;
    }
    /**
     * Refreshes the spots displayed on the map with the specified new spots.
     *
     * @param {Array} newSpots - The new spots to display on the map.
     */
    function refreshSpots(newSpots) {
        const spotsChanged = mapUtils.haveSpotsChanged({
            newSpots,
            prevSpots: spots,
        });
        if (spotsChanged) {
            const { removed, updated } = getSpotChanges(newSpots);
            // first remove the markers
            removed.forEach(({ spotId }) => {
                removeSpotMarker(spotId);
            });
            // then add the new and updated markers
            updated.forEach(spot => {
                addSpotMarker(spot);
            });
            // update the spots
            spots = newSpots;
            if (needsFitToBounds) {
                fitMapToMarkers();
            }
        }
    }
    /**
     * Adds a marker for the specified spot to the map.
     *
     * @param {object} spot - The spot to add a marker for.
     * @returns {object} The marker that was added.
     */
    function addSpotMarker(spot) {
        const { spotId } = spot;
        if (markers[spotId]) {
            return;
        }
        markers[spotId] = mapUtils.addMarker({
            spot,
            map,
            maps,
            getZIndex,
            infoWindow,
            onClick: onMarkerClicked,
        });
        // hide marker if it is the preview spot
        if (isPreviewSpotId(spotId)) {
            mapUtils.hideMarker({ marker: markers[spotId] });
        }
    }
    function isPreviewSpotId(spotId) {
        return previewSpot && `${previewSpot.spotId}` === `${spotId}`;
    }
    /*
     * Handle click on a marker.
     * @param {Spot} spot - The spot.
     */
    function onMarkerClicked(spot) {
        onPreviewSpot(spot);
        trackPinPreviewClicked({
            spotId: spot.spotId,
        });
    }
    /**
     * Removes the marker for the specified spot from the map.
     *
     * @param {string} spotId - The ID of the spot to remove the marker for.
     */
    function removeSpotMarker(spotId) {
        const marker = markers[spotId];
        mapUtils.removeMarker({ marker, maps });
        delete markers[spotId];
    }
    /**
     * Removes all spot markers from the map.
     */
    function removeAllMarkers() {
        spots.forEach(({ spotId }) => {
            removeSpotMarker(spotId);
        });
    }
    /**
     * Callback function to be called when the user starts dragging the map.
     */
    function onDragStart() {
        isDragging = true;
        if (isDraggingTimeout) {
            clearTimeout(isDraggingTimeout);
            isDraggingTimeout = null;
        }
    }
    /**
     * Callback function to be called when the user stops dragging the map.
     */
    function onDragEnd() {
        isDraggingTimeout = setTimeout(() => {
            isDragging = false;
            closeInfoWindow();
            onPan();
        }, 500);
    }
    /**
     * Fits the map to the markers displayed on it.
     */
    function fitMapToMarkers() {
        needsFitToBounds = false;
        if (spots.length > 0) {
            const distance = getDistanceOfTheSpotsFromDestination();
            isAutoZooming = true;
            mapUtils.fitMapToMarkers({
                distance,
                center: centerLatLng,
                maps,
                map,
                idleCallback: () => {
                    onZoomChanged();
                    isAutoZooming = false;
                },
            });
        }
    }
    /**
     * Callback function to be called when the user zooms in or out of the map.
     */
    function onZoomChanged() {
        const options = mapUtils.getMapOptions({
            map,
            center: centerLatLng,
        });
        // the 'zoom_changed' event gets fired in every zoom instance, so we have to determine what to do with zoom levels in parent
        onZoom({
            ...options,
            prevZoom,
            isAutoZooming,
        });
    }
    /**
     * Cleans up the Google Map instance by removing all event listeners and removing the onSpotHover listener from the document.
     */
    function cleanup() {
        if (maps && map) {
            removeAllMarkers();
            mapUtils.cleanupMap({
                map,
                maps,
                onSpotHover,
            });
        }
        if (isDraggingTimeout) {
            clearTimeout(isDraggingTimeout);
        }
        if (zoomThrottle) {
            zoomThrottle.cancel();
        }
    }
    /**
     * Gets the changes in spots between the previous and new spots.
     *
     * @param {Array} newSpots - The new spots to compare with the previous spots.
     * @returns {object} An object containing the removed and updated spots.
     */
    function getSpotChanges(newSpots) {
        const prevSpots = spots;
        const removed = differenceWith(prevSpots, newSpots, (a, b) => a.spotId === b.spotId &&
            a.markerLabelText === b.markerLabelText &&
            a.recentlyViewed === b.recentlyViewed &&
            a.visualFlags?.map(flag => flag.type).join() ===
                b.visualFlags?.map(flag => flag.type).join());
        const added = newSpots.filter(newSpot => prevSpots.every(prevSpot => prevSpot.spotId !== newSpot.spotId));
        const updated = newSpots.filter(newSpot => prevSpots.some(prevSpot => newSpot.spotId === prevSpot.spotId &&
            (newSpot.markerLabelText !== prevSpot.markerLabelText ||
                newSpot.recentlyViewed !== prevSpot.recentlyViewed ||
                newSpot.visualFlags?.map(flag => flag.type).join() !==
                    prevSpot.visualFlags?.map(flag => flag.type).join())));
        return { removed, updated: [...added, ...updated] };
    }
    /**
     * Gets the distance from the destination to the spot that contains ~50% of spots.
     *
     * @returns {number} The distance from the destination to the spot that contains ~50% of spots.
     */
    function getDistanceOfTheSpotsFromDestination() {
        const sortedByDistance = spots.sort((a, b) => a.distanceFromDestination - b.distanceFromDestination);
        // get the distance from the destination
        // to the spot that contains ~50% of spots
        // or 90% of spots when showAllSpots flag is set
        const spotsToShow = showAllSpots ? 0.9 : 0.5;
        const index = Math.floor(spots.length * spotsToShow);
        return sortedByDistance[index].distanceFromDestination;
    }
    /**
     * Callback function to be called when a spot is hovered over.
     *
     * @param {object} evt - The event object containing information about the hovered spot.
     */
    function onSpotHover(evt) {
        const { detail: { hovered, spotId }, } = evt;
        if (!markers[spotId]) {
            return;
        }
        const { markerType } = spots.find(s => `${s.spotId}` === `${spotId}`);
        mapUtils.onSpotHover({
            hovered,
            markerType,
            marker: markers[spotId],
        });
    }
    /**
     * Adds a KML layer to the map.
     *
     * @param {string} kmlUrl - The URL of the KML file to add to the map.
     */
    function addKMLLayer(kmlUrl) {
        if (!kmlLayer) {
            kmlLayer = mapUtils.addKMLLayer({
                map,
                maps,
                kmlUrl,
            });
        }
    }
    function closeInfoWindow() {
        if (previewSpot) {
            // clear the preview spot before closing the info window
            onPreviewSpot(null);
            infoWindow.close();
        }
    }
    init();
    return {
        addKMLLayer,
        cleanup,
        getSpotChanges,
        refreshSpots,
        setCenterLatLngAndTitle,
        setSearchRequestId,
        setZoom,
        closeInfoWindow,
    };
};
/**
 * @typedef {object} Spot
 * @property {string} spotId - The ID of the spot.
 * @property {string} markerLabelText - The label text of the marker.
 * @property {boolean} recentlyViewed - Whether the spot was recently viewed or not.
 * @property {number} distanceFromDestination - The distance from the destination.
 */
/**
 * @typedef {object} LatLng
 * @property {number} lat - The latitude.
 * @property {number} lng - The longitude.
 */
export default googleMapWidget;
